
import Link from '@/components/compat/Link';
import { useRouter } from '@/lib/navigation';
import { useEffect, useRef, useState } from 'react';
import { ArrowLeft, Eye, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';
import type { CmsArticleStatus } from '@/lib/cms-types';
import {
  canFeatureGuide,
  canPublishContent,
  canSendGuideToReview,
  PUBLICATION_STATUS_BADGE_CLASS,
  PUBLICATION_STATUS_LABELS,
  resolveGuideStatusOnSave,
} from '@/lib/cms-publication';
import { resolveGuideSlug, getCmsArticleSlug } from '@/lib/cms-guides';
import type { ArticleContent } from '@/lib/articleContent';
import { serializeArticleBody, parseArticleBody } from '@/lib/cms-article-body';
import { painelSurfaceClass, painelSelectContentClass, painelButtonPrimaryClass, painelButtonOutlineClass, painelSwitchClass } from '@/components/painel/painel-styles';
import { useCms } from './cms/cms-context';
import { painelInputClass } from './cms/painel-form';
import { GuideBlocksEditor } from './cms/GuideBlocksEditor';
import { GuidePreviewDialog } from './cms/GuidePreviewDialog';
import { CMS_DEFAULT_ICON, CMS_ICON_OPTIONS, CmsCategoryIcon } from './cms/cms-icons';
import { usePainelSaveFeedback } from './PainelSaveFeedback';
import { usePainelSession } from '@/components/painel/rbac/painel-session-context';
import { findMockUserByUsername, sessionHasPermission } from '@/lib/painel-rbac';
import { logPainelEditorPublicationActivity, logPainelSubmitForReview } from '@/lib/painel-activity-log';
import { EntitySeoSection } from '@/components/painel/seo/EntitySeoSection';
import { SearchCode } from 'lucide-react';

type PainelArticleEditorPageProps =
  | { mode: 'new' }
  | { mode: 'edit'; articleId: string };

export function PainelArticleEditorPage(props: PainelArticleEditorPageProps) {
  const router = useRouter();
  const { showSaveSuccess } = usePainelSaveFeedback();
  const { session, role } = usePainelSession();
  const {
    categories,
    articles,
    hydrated,
    adminHydrated,
    addArticle,
    updateArticle,
    getArticleById,
    getCategoryById,
  } = useCms();

  const [previewOpen, setPreviewOpen] = useState(false);

  const [articleBody, setArticleBody] = useState<ArticleContent>({ intro: '', blocks: [] });
  const [title, setTitle] = useState('');
  const [categoryId, setCategoryId] = useState<string>('');
  const [publishRequested, setPublishRequested] = useState(false);
  const [featured, setFeatured] = useState(false);
  const [icon, setIcon] = useState<string>('');
  const seededArticleIdRef = useRef<string | null>(null);
  const canPublish = canPublishContent(role);
  const canManageFeatured = canFeatureGuide(session);
  const previewStatus: CmsArticleStatus = !publishRequested
    ? 'draft'
    : canPublish
      ? 'published'
      : 'pending_review';

  useEffect(() => {
    if (!hydrated || !adminHydrated || props.mode !== 'edit') return;
    if (seededArticleIdRef.current === props.articleId) return;
    const row = getArticleById(props.articleId);
    if (row) {
      setTitle(row.title);
      setCategoryId(row.categoryId);
      setArticleBody(parseArticleBody(row.content));
      setPublishRequested(row.status === 'published' || row.status === 'pending_review');
      setFeatured(Boolean(row.featured));
      setIcon(row.icon?.trim() || '');
      seededArticleIdRef.current = props.articleId;
    }
  }, [hydrated, adminHydrated, props, getArticleById]);

  const selectTriggerClass = cn(
    painelInputClass,
    'flex items-center justify-between text-left [&>span]:line-clamp-1'
  );

  const handleSave = async () => {
    const t = title.trim();
    if (!t || !categoryId) return;
    const stored = serializeArticleBody(articleBody);
    const nextStatus = resolveGuideStatusOnSave(role, publishRequested);
    const mockUser = session ? findMockUserByUsername(session.username) : null;
    const actorPatch = session
      ? {
          updatedByName: session.name,
          ...(mockUser ? { updatedById: mockUser.id } : {}),
        }
      : {};
    const existing = props.mode === 'edit' ? getArticleById(props.articleId) : undefined;
    const slug =
      existing?.slug?.trim() ||
      resolveGuideSlug(
        t,
        categoryId,
        articles,
        props.mode === 'edit' ? props.articleId : undefined
      );

    const featuredPatch = canManageFeatured ? { featured } : {};
    const iconPatch = { icon: icon.trim() || null };

    try {
      if (props.mode === 'new') {
        const created = await addArticle({
          title: t,
          categoryId,
          content: stored,
          status: nextStatus,
          slug,
          ...featuredPatch,
          ...iconPatch,
          ...actorPatch,
        });
        logPainelEditorPublicationActivity(session, {
          resource: 'guide',
          resourceId: created.id,
          resourceLabel: t,
          isNew: true,
          previousStatus: null,
          nextStatus,
        });
        showSaveSuccess({
          title: 'Guia criado',
          description:
            nextStatus === 'pending_review'
              ? 'O guia foi enviado para revisão antes de ir ao ar.'
              : nextStatus === 'published'
                ? 'O guia foi publicado no site.'
                : 'O guia foi guardado como rascunho.',
        });
        router.replace(`/painel/guias/${created.id}/edit`);
      } else {
        await updateArticle(props.articleId, {
          title: t,
          categoryId,
          content: stored,
          status: nextStatus,
          slug,
          ...featuredPatch,
          ...iconPatch,
          ...actorPatch,
        });
        logPainelEditorPublicationActivity(session, {
          resource: 'guide',
          resourceId: props.articleId,
          resourceLabel: t,
          isNew: false,
          previousStatus: existing?.status ?? null,
          nextStatus,
        });
        showSaveSuccess({
          title: 'Guia guardado',
          description:
            nextStatus === 'pending_review'
              ? 'O guia foi enviado para revisão antes de ir ao ar.'
              : nextStatus === 'published'
                ? 'O guia foi publicado no site.'
                : 'O guia foi guardado como rascunho.',
        });
      }
    } catch {
      /* toast já emitido no CMS */
    }
  };

  if (props.mode === 'edit' && (!hydrated || !adminHydrated)) {
    return (
      <div className="font-inter text-sm text-slate-400">Carregando…</div>
    );
  }

  if (props.mode === 'edit' && hydrated && adminHydrated) {
    const row = getArticleById(props.articleId);
    if (!row) {
      return (
        <div className="space-y-4">
          <h1 className="font-poppins text-2xl font-bold text-white">Guia não encontrado</h1>
          <p className="text-sm text-slate-400">
            O identificador não existe ou foi removido.
          </p>
          <Button asChild variant="outline" className="rounded-xl border-white/15 text-blue-100">
            <Link href="/painel/guias">Voltar aos guias</Link>
          </Button>
        </div>
      );
    }
  }

  const noCategories = categories.length === 0;
  const editingRow = props.mode === 'edit' && hydrated ? getArticleById(props.articleId) : undefined;
  const canReturnToReview =
    props.mode === 'edit' &&
    editingRow?.status === 'published' &&
    canSendGuideToReview(role);

  const saveButtonLabel = !publishRequested
    ? 'Salvar'
    : canPublish
      ? 'Publicar'
      : 'Enviar para revisão';

  const handleReturnToReview = async () => {
    if (props.mode !== 'edit' || !editingRow) return;
    try {
      await updateArticle(props.articleId, { status: 'pending_review' });
      logPainelSubmitForReview(session, {
        resource: 'guide',
        resourceId: props.articleId,
        resourceLabel: editingRow.title,
      });
      setPublishRequested(true);
      showSaveSuccess({
        title: 'Enviado para revisão',
        description: 'O guia foi retirado do ar e voltou à fila de revisão.',
      });
    } catch {
      /* toast já emitido no CMS */
    }
  };

  return (
    <>
      <GuidePreviewDialog
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        title={title}
        categoryLabel={categoryId ? getCategoryById(categoryId)?.name : undefined}
        body={articleBody}
      />
      <div className="w-full max-w-none space-y-8">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0 flex-1">
          <Button
            variant="ghost"
            asChild
            className="mb-2 -ml-2 rounded-xl text-slate-300 hover:bg-white/10 hover:text-white"
          >
            <Link href="/painel/guias">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Guias
            </Link>
          </Button>
          <h1 className="font-poppins text-2xl font-bold tracking-tight text-white md:text-3xl">
            {props.mode === 'new' ? 'Novo guia' : 'Editar guia'}
          </h1>
          <p className="mt-1 font-inter text-sm text-slate-400">
            Estruture o guia com blocos como no site público; alterações publicadas substituem o texto
            estático.
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          className="shrink-0 rounded-xl border-white/20 bg-white/[0.06] text-blue-100 hover:bg-white/10"
          onClick={() => setPreviewOpen(true)}
          disabled={noCategories}
        >
          <Eye className="mr-2 h-4 w-4" />
          Pré-visualização
        </Button>
      </div>

      <div
        className={cn(
          painelSurfaceClass,
          'space-y-6 p-6 md:p-8'
        )}
      >
        {noCategories ? (
          <div className="rounded-xl border border-amber-400/25 bg-amber-500/10 p-4 text-sm text-amber-100/90">
            Precisa de pelo menos uma categoria.{' '}
            <Link href="/painel/categorias" className="font-semibold underline underline-offset-2">
              Gerir categorias
            </Link>
          </div>
        ) : null}

        <div className="grid gap-2">
          <Label htmlFor="article-title" className="text-blue-100/90">
            Título
          </Label>
          <input
            id="article-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className={painelInputClass}
            placeholder="Título do guia"
            disabled={noCategories}
          />
        </div>

        <div className="grid gap-2">
          <Label className="text-blue-100/90">Categoria</Label>
          <Select
            value={categoryId || undefined}
            onValueChange={setCategoryId}
            disabled={noCategories}
          >
            <SelectTrigger className={selectTriggerClass}>
              <SelectValue placeholder="Escolher categoria" />
            </SelectTrigger>
            <SelectContent className={painelSelectContentClass}>
              {categories.map((c) => (
                <SelectItem
                  key={c.id}
                  value={c.id}
                  className="focus:bg-white/10 focus:text-white"
                >
                  {c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="grid gap-2">
          <div className="flex flex-wrap items-end justify-between gap-2">
            <div>
              <Label className="text-blue-100/90">Ícone do guia</Label>
              <p className="mt-0.5 font-inter text-xs text-slate-400">
                Opcional. Dá identidade visual ao card na categoria e em «Continue explorando».
              </p>
            </div>
            {icon ? (
              <button
                type="button"
                onClick={() => setIcon('')}
                className="font-inter text-xs font-medium text-sky-300/90 transition-colors hover:text-sky-200"
                disabled={noCategories}
              >
                Remover ícone
              </button>
            ) : null}
          </div>
          <div className="grid grid-cols-6 gap-1.5 sm:grid-cols-8 md:grid-cols-10">
            {CMS_ICON_OPTIONS.map((opt) => {
              const selected = icon === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setIcon(opt.value)}
                  disabled={noCategories}
                  className={cn(
                    'inline-flex h-10 w-full items-center justify-center rounded-xl border transition-all',
                    selected
                      ? 'border-sky-400/50 bg-sky-500/15 text-sky-100 ring-1 ring-sky-400/30'
                      : 'border-white/10 bg-white/[0.03] text-blue-100/75 hover:border-white/20 hover:bg-white/[0.06] hover:text-white',
                    noCategories && 'pointer-events-none opacity-50'
                  )}
                  aria-pressed={selected}
                  aria-label={opt.label}
                  title={opt.label}
                >
                  <CmsCategoryIcon name={opt.value} className="h-[18px] w-[18px]" />
                </button>
              );
            })}
          </div>
          {icon ? (
            <p className="inline-flex items-center gap-2 font-inter text-xs text-blue-100/70">
              Selecionado:
              <CmsCategoryIcon name={icon || CMS_DEFAULT_ICON} className="h-4 w-4" />
              <span className="text-blue-100/90">{CMS_ICON_OPTIONS.find((o) => o.value === icon)?.label ?? icon}</span>
            </p>
          ) : (
            <p className="font-inter text-xs text-slate-500">
              Sem ícone próprio — o card usa o ícone da categoria.
            </p>
          )}
        </div>

        <GuideBlocksEditor
          value={articleBody}
          onChange={setArticleBody}
          disabled={noCategories}
        />

        <div className="flex flex-wrap items-center justify-between gap-4 rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3">
          <div>
            <p className="font-poppins text-sm font-medium text-blue-50">Publicação</p>
            <p className="font-inter text-xs text-slate-400">
              {canPublish
                ? 'Ligado: publica diretamente no site. Desligado: mantém como rascunho.'
                : 'Ligado: envia para a fila de Revisão. Desligado: rascunho só no painel.'}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <span
              className={cn(
                'inline-flex rounded-full px-2.5 py-0.5 font-poppins text-[11px] font-semibold uppercase tracking-wide ring-1',
                PUBLICATION_STATUS_BADGE_CLASS[previewStatus]
              )}
            >
              {PUBLICATION_STATUS_LABELS[previewStatus]}
            </span>
            <Switch
              checked={publishRequested}
              onCheckedChange={setPublishRequested}
              disabled={noCategories}
              className={painelSwitchClass}
            />
          </div>
        </div>

        {canManageFeatured ? (
          <div className="flex flex-wrap items-center justify-between gap-4 rounded-xl border border-amber-400/20 bg-amber-500/[0.07] px-4 py-3">
            <div>
              <p className="font-poppins text-sm font-medium text-amber-50">
                Destacar como artigo popular
              </p>
              <p className="font-inter text-xs text-amber-100/70">
                Exibe a badge amarela «Artigo Popular» no site. Controlo manual — não é automático.
              </p>
            </div>
            <Switch
              checked={featured}
              onCheckedChange={setFeatured}
              disabled={noCategories}
              className={painelSwitchClass}
              aria-label="Destacar como artigo popular"
            />
          </div>
        ) : null}

        {sessionHasPermission(session, 'seo.manage') ? (
          <div className={cn(painelSurfaceClass, 'space-y-3 p-4 sm:p-5')}>
            <div className="flex items-center gap-2">
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-sky-500/15 text-sky-300 ring-1 ring-sky-400/25">
                <SearchCode className="h-4 w-4" aria-hidden />
              </span>
              <div>
                <p className="font-poppins text-sm font-semibold text-white">SEO</p>
                <p className="font-inter text-xs text-slate-500">
                  Metadados deste guia (title, description, Open Graph, robots).
                </p>
              </div>
            </div>
            <EntitySeoSection
              entityType="guide"
              entityId={
                props.mode === 'edit' && /^\d+$/.test(props.articleId)
                  ? Number(props.articleId)
                  : null
              }
              enabled={props.mode === 'edit' && /^\d+$/.test(props.articleId)}
              fallbackTitle={title}
              previewPath={
                categoryId
                  ? `/${getCategoryById(categoryId)?.id ?? categoryId}/${
                      props.mode === 'edit'
                        ? (getArticleById(props.articleId)
                            ? getCmsArticleSlug(getArticleById(props.articleId)!)
                            : resolveGuideSlug(title, categoryId, articles, props.articleId))
                        : resolveGuideSlug(title, categoryId, articles)
                    }`
                  : '/'
              }
            />
          </div>
        ) : null}

        <div className="flex flex-wrap gap-3 pt-2">
          <Button
            type="button"
            className={painelButtonPrimaryClass}
            onClick={handleSave}
            disabled={noCategories || !title.trim() || !categoryId}
          >
            {saveButtonLabel}
          </Button>
          {canReturnToReview ? (
            <Button
              type="button"
              variant="outline"
              className={painelButtonOutlineClass}
              onClick={handleReturnToReview}
            >
              <RotateCcw className="mr-2 h-4 w-4" />
              Enviar para revisão
            </Button>
          ) : null}
          <Button
            type="button"
            variant="outline"
            className={painelButtonOutlineClass}
            asChild
          >
            <Link href="/painel/guias">Cancelar</Link>
          </Button>
        </div>
      </div>
    </div>
    </>
  );
}
